/**
 * Boomfection Game Core - Browser Bundle (Rendering Only)
 *
 * Spiellogik wird vom Server verwaltet (Server-Authority).
 * Dieses Modul enthält nur Datenstrukturen für Rendering und Server-Sync.
 *
 * Konstanten-Quelle: src/game/config/GameConstants.js
 *
 * @copyright reDim GmbH | @link https://www.redim.de | @contact team@redim.de
 */

import { t } from '/js/i18n.js';
import { generateSphereGeometry } from '/js/game/shared/SphereGeometry.js';
import { VIRUS_TYPE_IDS } from '/js/game/shared/VirusTypes.js';

// ============================================================================
// CONSTANTS (Mirror of src/game/config/GameConstants.js)
// ============================================================================

/**
 * Sphere-Konstanten für Geometrie-Generierung
 * @see src/game/config/GameConstants.js
 */
const SPHERE = {
    FIELD_INSET: 0.92,
    LLOYD_ITERATIONS: 5,
    DEFAULT_RADIUS: 1,
    DEFAULT_SUBDIVISIONS: 3
};

// ============================================================================
// PLAYER COLORS & VIRUS TYPES
// ============================================================================

export const PLAYER_COLORS = [
    { id: 'red', name: 'Rot', hex: '#e63946', rgb: [230, 57, 70] },
    { id: 'blue', name: 'Blau', hex: '#4361ee', rgb: [67, 97, 238] },
    { id: 'green', name: 'Grün', hex: '#2a9d8f', rgb: [42, 157, 143] },
    { id: 'yellow', name: 'Gelb', hex: '#f4a261', rgb: [244, 162, 97] },
    { id: 'purple', name: 'Lila', hex: '#9b5de5', rgb: [155, 93, 229] },
    { id: 'cyan', name: 'Cyan', hex: '#00b4d8', rgb: [0, 180, 216] },
    { id: 'pink', name: 'Pink', hex: '#ff6b9d', rgb: [255, 107, 157] },
    { id: 'lime', name: 'Lime', hex: '#7cb518', rgb: [124, 181, 24] },
    { id: 'orange', name: 'Orange', hex: '#ff7b00', rgb: [255, 123, 0] },
    { id: 'gold', name: 'Gold', hex: '#d4a017', rgb: [212, 160, 23] }
];

// Kanonische Liste aus dem geteilten Modul (Single Source of Truth);
// Anzeigenamen kommen aus i18n (lobby.virusTypes.<id>).
export const VIRUS_TYPES = VIRUS_TYPE_IDS.map((id) => ({ id }));

/**
 * V-024: Farbenblind-sichere Paletten (Wong)
 * @see src/game/config/GameConstants.js
 */
export const COLORBLIND_PALETTES = {
    none: null,
    deuteranopia: [
        { id: 'cb1', name: 'Blau', hex: '#0072B2', rgb: [0, 114, 178] },
        { id: 'cb2', name: 'Orange', hex: '#E69F00', rgb: [230, 159, 0] },
        { id: 'cb3', name: 'Rosa', hex: '#CC79A7', rgb: [204, 121, 167] },
        { id: 'cb4', name: 'Gruen', hex: '#009E73', rgb: [0, 158, 115] },
        { id: 'cb5', name: 'Gelb', hex: '#F0E442', rgb: [240, 228, 66] },
        { id: 'cb6', name: 'Hellblau', hex: '#56B4E9', rgb: [86, 180, 233] }
    ],
    protanopia: [
        { id: 'cb1', name: 'Blau', hex: '#0072B2', rgb: [0, 114, 178] },
        { id: 'cb2', name: 'Orange', hex: '#E69F00', rgb: [230, 159, 0] },
        { id: 'cb3', name: 'Rosa', hex: '#CC79A7', rgb: [204, 121, 167] },
        { id: 'cb4', name: 'Gruen', hex: '#009E73', rgb: [0, 158, 115] },
        { id: 'cb5', name: 'Gelb', hex: '#F0E442', rgb: [240, 228, 66] },
        { id: 'cb6', name: 'Hellblau', hex: '#56B4E9', rgb: [86, 180, 233] }
    ],
    tritanopia: [
        { id: 'cb1', name: 'Orange', hex: '#E69F00', rgb: [230, 159, 0] },
        { id: 'cb2', name: 'Hellblau', hex: '#56B4E9', rgb: [86, 180, 233] },
        { id: 'cb3', name: 'Rosa', hex: '#CC79A7', rgb: [204, 121, 167] },
        { id: 'cb4', name: 'Gruen', hex: '#009E73', rgb: [0, 158, 115] },
        { id: 'cb5', name: 'Rot', hex: '#D55E00', rgb: [213, 94, 0] },
        { id: 'cb6', name: 'Blau', hex: '#0072B2', rgb: [0, 114, 178] }
    ]
};

/**
 * Gibt die aktiven Spielerfarben zurueck (Standard oder Farbenblind-Palette)
 * @returns {Array} Farb-Array
 */
export function getActivePlayerColors() {
    const mode = localStorage.getItem('boomfection_colorblind_mode') || 'none';
    const palette = COLORBLIND_PALETTES[mode];
    return palette || PLAYER_COLORS;
}

export const GameState = {
    LOBBY: 'lobby',
    STARTING: 'starting',
    PLAYING: 'playing',
    EXPLOSION: 'explosion',
    PAUSED: 'paused',
    FINISHED: 'finished'
};

// ============================================================================
// FIELD CLASS - Daten-Container für Rendering
// ============================================================================

export class Field {
    /**
     * @param {Object} config
     * @param {number} config.id - Eindeutige Feld-ID
     * @param {string} config.type - 'hexagon' oder 'pentagon'
     * @param {number[]} config.neighbors - Array der Nachbar-IDs
     * @param {number[]} config.center - [x, y, z] Position
     * @param {number[]} config.centerNormalized - Normalisierte Position
     * @param {number[][]} config.vertices - Polygon-Vertices
     */
    constructor(config) {
        this.id = config.id;
        this.type = config.type;
        this.neighbors = config.neighbors || [];
        this.center = config.center || [0, 0, 0];
        this.centerNormalized = config.centerNormalized || this.center;
        this.vertices = config.vertices || [];

        // Spielzustand (vom Server synchronisiert)
        this.ownerId = null;
        this.value = 0;
        this.isBlocked = false;
        this.pill = null;

        // Explosions-Schwelle basierend auf Nachbar-Anzahl
        this.maxValue = this.neighbors.length;
        this.explosionThreshold = this.maxValue + 1;
    }

    // Rendering-Hilfsmethoden
    isNeutral() {
        return this.ownerId === null;
    }
    isOwnedBy(playerId) {
        return this.ownerId === playerId;
    }
    willExplode() {
        return this.value >= this.explosionThreshold;
    }

    /**
     * Setzt Feld auf Server-Zustand zurück
     */
    reset() {
        this.ownerId = null;
        this.value = 0;
    }

    /**
     * Serialisiert Feld-Zustand
     * @returns {Object}
     */
    serialize() {
        return {
            id: this.id,
            type: this.type,
            ownerId: this.ownerId,
            value: this.value,
            pill: this.pill
        };
    }

    /**
     * Aktualisiert Feld mit Server-Daten
     * @param {Object} data
     */
    deserialize(data) {
        if (data.ownerId !== undefined) this.ownerId = data.ownerId;
        if (data.value !== undefined) this.value = data.value;
        if (data.isBlocked !== undefined) this.isBlocked = data.isBlocked;
        if (data.pill !== undefined) this.pill = data.pill;
        // Strukturdaten vom Server übernehmen
        if (data.maxValue !== undefined) {
            this.maxValue = data.maxValue;
            this.explosionThreshold = this.maxValue + 1;
        }
        if (data.neighbors !== undefined) {
            this.neighbors = data.neighbors;
        }
        // Geometrie-Daten für Kamera-Rotation
        if (data.center !== undefined) {
            this.center = data.center;
        }
        if (data.centerNormalized !== undefined) {
            this.centerNormalized = data.centerNormalized;
        }
        if (data.vertices !== undefined) {
            this.vertices = data.vertices;
        }
    }
}

// ============================================================================
// PLAYER CLASS - Daten-Container für Rendering
// ============================================================================

export class Player {
    /**
     * @param {Object} config
     * @param {string} config.id - Eindeutige Spieler-ID
     * @param {string} config.name - Anzeigename
     * @param {string} config.colorId - Farb-ID aus PLAYER_COLORS
     * @param {string} config.virusType - Virus-Typ aus VIRUS_TYPES
     */
    constructor(config) {
        this.id = config.id;
        this.name = config.name || t('game.defaultPlayerNumbered', { n: config.id });
        this.colorId = config.colorId || 'red';
        this.virusType = config.virusType || 'classic';
        this.isAI = config.isAI || false;
        this.aiDifficulty = config.aiDifficulty || 'normal';

        // Team-Zuordnung (null = kein Team, 0 oder 1 fuer Team-Modus)
        this.teamId = config.teamId !== undefined ? config.teamId : null;

        // Farb-Objekt aus ID (beruecksichtigt Farbenblind-Modus)
        this.color = this._resolveColor(this.colorId);

        // Spielzustand (vom Server synchronisiert)
        this.isActive = true;
        this.isEliminated = false;
        this.isSpectator = false;
        this.isConnected = true;
        this.fieldCount = 0;
        this.totalViruses = 0;
        this.points = 0;

        // Statistiken
        this.stats = {
            fieldsConquered: 0,
            explosionsCaused: 0,
            playersEliminated: 0,
            turnsPlayed: 0
        };
    }

    /**
     * Loest die Farbe anhand der colorId auf.
     * Beruecksichtigt aktive Farbenblind-Palette.
     * @param {string} colorId
     * @returns {Object} Farb-Objekt { id, name, hex, rgb }
     * @private
     */
    _resolveColor(colorId) {
        const activeColors = getActivePlayerColors();
        return (
            activeColors.find((c) => c.id === colorId) ||
            PLAYER_COLORS.find((c) => c.id === colorId) ||
            PLAYER_COLORS[0]
        );
    }

    /**
     * Aktualisiert die Spielerfarbe basierend auf aktiver Palette.
     * Wird aufgerufen wenn sich der Farbenblind-Modus aendert.
     */
    refreshColor() {
        this.color = this._resolveColor(this.colorId);
    }

    /**
     * Eliminiert den Spieler
     */
    eliminate() {
        this.isActive = false;
        this.isEliminated = true;
        this.fieldCount = 0;
        this.totalViruses = 0;
    }

    /**
     * Serialisiert Spieler-Zustand
     * @param {boolean} includePrivate - Private Daten einschließen
     * @returns {Object}
     */
    serialize(includePrivate = false) {
        const data = {
            id: this.id,
            name: this.name,
            colorId: this.colorId,
            color: this.color,
            virusType: this.virusType,
            isAI: this.isAI,
            isActive: this.isActive,
            isEliminated: this.isEliminated,
            fieldCount: this.fieldCount,
            totalViruses: this.totalViruses,
            isConnected: this.isConnected,
            teamId: this.teamId
        };
        if (includePrivate) {
            data.stats = { ...this.stats };
            data.aiDifficulty = this.aiDifficulty;
        }
        return data;
    }

    /**
     * Aktualisiert Spieler mit Server-Daten
     * @param {Object} data
     */
    deserialize(data) {
        if (data.name !== undefined) this.name = data.name;
        if (data.colorId !== undefined) {
            this.colorId = data.colorId;
            this.color = this._resolveColor(this.colorId);
        }
        if (data.color !== undefined) this.color = data.color;
        if (data.virusType !== undefined) this.virusType = data.virusType;
        if (data.isAI !== undefined) this.isAI = data.isAI;
        if (data.aiDifficulty !== undefined) this.aiDifficulty = data.aiDifficulty;
        if (data.isActive !== undefined) this.isActive = data.isActive;
        if (data.isEliminated !== undefined) this.isEliminated = data.isEliminated;
        if (data.isSpectator !== undefined) this.isSpectator = data.isSpectator;
        if (data.fieldCount !== undefined) this.fieldCount = data.fieldCount;
        if (data.totalViruses !== undefined) this.totalViruses = data.totalViruses;
        if (data.points !== undefined) this.points = data.points;
        if (data.isConnected !== undefined) this.isConnected = data.isConnected;
        if (data.teamId !== undefined) this.teamId = data.teamId;
        if (data.stats) this.stats = { ...data.stats };
    }
}

// ============================================================================
// SPHERE CLASS - Geometrie-Generierung für Rendering
// ============================================================================

export class Sphere {
    /**
     * @param {Object} config
     * @param {number} config.subdivisions - Unterteilungsgrad (1-5)
     * @param {number} config.radius - Radius der Sphäre
     */
    constructor(config = {}) {
        this.subdivisions = config.subdivisions || SPHERE.DEFAULT_SUBDIVISIONS;
        this.radius = config.radius || SPHERE.DEFAULT_RADIUS;
        this.fields = new Map();
        this.stats = { hexagons: 0, pentagons: 0, totalFields: 0 };

        /**
         * Player-Feld-Index für O(1) Zugriff
         * @type {Map<string, Set<number>>}
         */
        this.playerFieldIndex = new Map();

        /** @type {Set<number>} Neutrale Feld-IDs */
        this.neutralFieldIds = new Set();

        this.generate();
    }

    /**
     * Generiert die geodätische Sphäre über das geteilte SphereGeometry-Modul.
     * Die Topologie (Feld-IDs/Typ/Nachbarn) ist bit-identisch mit der
     * server-autoritativen Sphere.js — abgesichert durch sphere-geometry.test.js.
     * Client-spezifisch ist nur der FIELD_INSET (Render-Lücke zwischen Feldern).
     */
    generate() {
        const geometry = generateSphereGeometry({
            subdivisions: this.subdivisions,
            lloydIterations: SPHERE.LLOYD_ITERATIONS,
            radius: this.radius,
            fieldInset: SPHERE.FIELD_INSET
        });

        this.fields.clear();
        for (const data of geometry) {
            this.fields.set(
                data.id,
                new Field({
                    id: data.id,
                    type: data.type,
                    neighbors: data.neighbors,
                    center: data.center,
                    centerNormalized: data.centerNormalized,
                    vertices: data.vertices
                })
            );
        }

        this.updateStats();
        this.rebuildIndex();
    }

    /**
     * Aktualisiert Statistiken
     */
    updateStats() {
        let hexCount = 0,
            pentCount = 0;
        for (const field of this.fields.values()) {
            if (field.type === 'hexagon') hexCount++;
            else pentCount++;
        }
        this.stats = {
            hexagons: hexCount,
            pentagons: pentCount,
            totalFields: this.fields.size
        };
    }

    // ========================================================================
    // Hilfsmethoden für Rendering und UI
    // ========================================================================

    getField(fieldId) {
        return this.fields.get(fieldId);
    }

    /**
     * Aktualisiert den Player-Index bei Owner-Änderung
     * @param {number} fieldId
     * @param {string|null} previousOwner
     * @param {string|null} newOwner
     */
    updateFieldOwnership(fieldId, previousOwner, newOwner) {
        // Vom alten Besitzer entfernen
        if (previousOwner !== null) {
            const oldSet = this.playerFieldIndex.get(previousOwner);
            if (oldSet) {
                oldSet.delete(fieldId);
                if (oldSet.size === 0) {
                    this.playerFieldIndex.delete(previousOwner);
                }
            }
        } else {
            this.neutralFieldIds.delete(fieldId);
        }

        // Zum neuen Besitzer hinzufügen
        if (newOwner !== null) {
            if (!this.playerFieldIndex.has(newOwner)) {
                this.playerFieldIndex.set(newOwner, new Set());
            }
            this.playerFieldIndex.get(newOwner).add(fieldId);
        } else {
            this.neutralFieldIds.add(fieldId);
        }
    }

    /**
     * Holt alle Felder eines Spielers (O(1) via Index)
     */
    getPlayerFields(playerId) {
        const fieldIds = this.playerFieldIndex.get(playerId);
        if (!fieldIds || fieldIds.size === 0) return [];

        const result = [];
        for (const id of fieldIds) {
            const field = this.fields.get(id);
            if (field) result.push(field);
        }
        return result;
    }

    /**
     * Holt alle neutralen Felder (O(1) via Index)
     */
    getNeutralFields() {
        const result = [];
        for (const id of this.neutralFieldIds) {
            const field = this.fields.get(id);
            if (field) result.push(field);
        }
        return result;
    }

    /**
     * Anzahl der Felder eines Spielers (O(1))
     */
    getPlayerFieldCount(playerId) {
        const fieldIds = this.playerFieldIndex.get(playerId);
        return fieldIds ? fieldIds.size : 0;
    }

    getExpandableFields(playerId) {
        const playerFields = this.getPlayerFields(playerId);
        const expandable = new Set();
        for (const field of playerFields) {
            for (const neighborId of field.neighbors) {
                const neighbor = this.getField(neighborId);
                if (neighbor && neighbor.isNeutral() && !neighbor.isBlocked) {
                    expandable.add(neighbor);
                }
            }
        }
        return [...expandable];
    }

    getClickableFields(playerId, isPlacementPhase = false) {
        // In der Platzierungsphase: alle neutralen, nicht-blockierten Felder
        if (isPlacementPhase) {
            return this.getNeutralFields().filter((f) => !f.isBlocked);
        }
        // Normale Regeln: eigene nicht-blockierte Felder + neutrale Nachbarn
        return [...this.getPlayerFields(playerId).filter((f) => !f.isBlocked), ...this.getExpandableFields(playerId)];
    }

    reset() {
        for (const field of this.fields.values()) {
            field.reset();
        }
        // Index zurücksetzen - alle werden neutral
        this.playerFieldIndex.clear();
        this.neutralFieldIds.clear();
        for (const field of this.fields.values()) {
            if (!field.isBlocked) {
                this.neutralFieldIds.add(field.id);
            }
        }
    }

    serializeFields() {
        return [...this.fields.values()].map((f) => f.serialize());
    }

    /**
     * Aktualisiert Felder mit Server-Daten
     * @param {Object[]} fieldsData
     */
    deserializeFields(fieldsData) {
        if (!fieldsData || !Array.isArray(fieldsData)) return;
        for (const fData of fieldsData) {
            const field = this.fields.get(fData.id);
            if (field) {
                // Index aktualisieren bei Owner-Änderung
                const previousOwner = field.ownerId;
                const newOwner = fData.ownerId !== undefined ? fData.ownerId : previousOwner;

                if (previousOwner !== newOwner) {
                    this.updateFieldOwnership(fData.id, previousOwner, newOwner);
                }

                field.deserialize(fData);
            }
        }
    }

    /**
     * Initialisiert den Index aus den aktuellen Feldern
     */
    rebuildIndex() {
        this.playerFieldIndex.clear();
        this.neutralFieldIds.clear();

        for (const field of this.fields.values()) {
            if (field.isBlocked) {
                continue; // Blockierte Felder weder neutral noch Spieler-Felder
            } else if (field.ownerId !== null) {
                if (!this.playerFieldIndex.has(field.ownerId)) {
                    this.playerFieldIndex.set(field.ownerId, new Set());
                }
                this.playerFieldIndex.get(field.ownerId).add(field.id);
            } else {
                this.neutralFieldIds.add(field.id);
            }
        }
    }
}

// ============================================================================
// GAME CLASS - Daten-Container für Spielzustand
// ============================================================================

export class Game {
    /**
     * @param {Object} config
     * @param {string} config.id - Spiel-ID
     * @param {number} config.subdivisions - Sphären-Unterteilung
     * @param {number} config.maxPlayers - Maximale Spieleranzahl
     */
    constructor(config = {}) {
        this.id = config.id || `game_${Date.now().toString(36)}${Math.random().toString(36).substring(2, 11)}`;
        this.createdAt = Date.now();

        this.config = {
            subdivisions: config.subdivisions || SPHERE.DEFAULT_SUBDIVISIONS,
            maxPlayers: config.maxPlayers || 4,
            isPublic: config.isPublic !== false,
            password: config.password || null,
            pillsEnabled: config.pillsEnabled || false,
            turnTimeLimit: config.turnTimeLimit || 0
        };

        // Spielfeld (lokal generiert für Rendering)
        this.sphere = new Sphere({
            subdivisions: this.config.subdivisions,
            radius: SPHERE.DEFAULT_RADIUS
        });

        // Spieler
        this.players = new Map();
        this.playerOrder = [];
        this.hostId = null;

        // Spielzustand (vom Server synchronisiert)
        this.state = GameState.LOBBY;
        this.currentRound = 0;
        this.currentPlayerIndex = 0;
        this.turnStartTime = null;

        // Event-Callbacks
        this.onStateChange = null;
        this.onPlayerJoin = null;
        this.onPlayerLeave = null;
        this.onTurnChange = null;
        this.onFieldUpdate = null;
        this.onExplosion = null;
        this.onPlayerEliminated = null;
        this.onGameEnd = null;

        // Spielergebnis
        this.winner = null;
        this.endTime = null;
    }

    // ========================================================================
    // UI-Hilfsmethoden
    // ========================================================================

    getCurrentPlayer() {
        if (this.playerOrder.length === 0) return null;
        return this.players.get(this.playerOrder[this.currentPlayerIndex]) || null;
    }

    // ========================================================================
    // Lokaler Hot-Seat / Tutorial Modus
    // ========================================================================

    /**
     * Startet das Spiel lokal (Hot-Seat / Tutorial).
     * Vereinfachte Version der Server-seitigen start()-Logik.
     * @returns {boolean}
     */
    start() {
        if (this.state !== GameState.LOBBY) {
            return false;
        }
        if (this.players.size < 2) {
            return false;
        }

        // Spielfeld zuruecksetzen
        this.sphere.reset();

        // Spielzustand setzen
        this.state = GameState.PLAYING;
        this.currentRound = 1;
        this.currentPlayerIndex = 0;
        this.turnStartTime = Date.now();

        // Eliminierungsrunde dynamisch berechnen
        this.eliminationStartRound = this.playerOrder.length <= 3 ? 2 : this.playerOrder.length <= 4 ? 3 : 4;

        if (this.onStateChange) {
            this.onStateChange(this.state);
        }
        if (this.onTurnChange) {
            this.onTurnChange(this.getCurrentPlayer());
        }

        return true;
    }

    /**
     * Fuehrt einen Zug lokal aus (Hot-Seat / Tutorial).
     * Vereinfachte Version der Server-seitigen makeMove()-Logik.
     * @param {string} playerId
     * @param {number} fieldId
     * @returns {Promise<Object>}
     */
    async makeMove(playerId, fieldId) {
        if (this.state !== GameState.PLAYING) {
            return { success: false, error: 'game_not_playing' };
        }

        const currentPlayer = this.getCurrentPlayer();
        if (!currentPlayer || currentPlayer.id !== playerId) {
            return { success: false, error: 'not_your_turn' };
        }

        const field = this.sphere.getField(fieldId);
        if (!field) {
            return { success: false, error: 'invalid_field' };
        }

        // Platzierungsphase: Spieler ohne Felder duerfen neutrales Feld waehlen
        const playerFields = this.sphere.getPlayerFields(playerId);
        const isPlacementPhase = this.currentRound === 1 && playerFields.length === 0;

        if (isPlacementPhase) {
            if (!field.isNeutral()) {
                return { success: false, error: 'field_not_neutral' };
            }
        } else {
            const clickable = this.sphere.getClickableFields(playerId);
            if (!clickable.find((f) => f.id === fieldId)) {
                return { success: false, error: 'field_not_clickable' };
            }
        }

        // Zug ausfuehren: Owner setzen und Value erhoehen
        const previousOwner = field.ownerId;
        field.ownerId = playerId;
        field.value++;

        // Ownership-Index aktualisieren
        if (previousOwner !== playerId) {
            this.sphere.updateFieldOwnership(fieldId, previousOwner, playerId);
        }

        if (this.onFieldUpdate) {
            this.onFieldUpdate(field);
        }

        // Explosion pruefen
        const willExplode = field.value >= field.explosionThreshold;
        if (willExplode) {
            await this._processLocalExplosions(field, playerId);

            if (this.state === GameState.FINISHED) {
                return { success: true, gameEnded: true, winner: this.winner };
            }
        }

        // Spielerstatistiken aktualisieren
        this._updateLocalPlayerStats();

        // Eliminierungen pruefen
        this._checkLocalEliminations();

        // Gewinner pruefen
        if (this._checkLocalWinner()) {
            return { success: true, gameEnded: true, winner: this.winner };
        }

        // Naechster Spieler
        this._nextLocalTurn();

        return { success: true, field: field.serialize() };
    }

    /**
     * Verarbeitet Explosionen lokal (vereinfacht, wellenbasiert)
     * @param {Object} initialField
     * @param {string} playerId
     */
    async _processLocalExplosions(initialField, playerId) {
        this.state = GameState.EXPLOSION;
        let fieldsToExplode = [initialField];
        let maxWaves = 100;

        while (fieldsToExplode.length > 0 && maxWaves-- > 0) {
            const nextWave = [];

            for (const explodingField of fieldsToExplode) {
                if (explodingField.value < explodingField.explosionThreshold) {
                    continue;
                }

                const _spreadValue = explodingField.value;
                explodingField.value = 0;
                explodingField.ownerId = null;
                this.sphere.updateFieldOwnership(explodingField.id, playerId, null);

                // Viren auf Nachbarn verteilen
                for (const neighborId of explodingField.neighbors) {
                    const neighbor = this.sphere.getField(neighborId);
                    if (!neighbor) {
                        continue;
                    }
                    // Blockierte Felder empfangen keine Viren (analog Server receivePulse:
                    // Pulse springt über blockierte Felder). Ohne diesen Check würden im
                    // lokalen Hot-Seat/Tutorial-Modus blockierte Felder befüllt/erobert.
                    if (neighbor.isBlocked) {
                        continue;
                    }

                    const prevOwner = neighbor.ownerId;
                    neighbor.ownerId = playerId;
                    neighbor.value++;

                    if (prevOwner !== playerId) {
                        this.sphere.updateFieldOwnership(neighborId, prevOwner, playerId);
                    }

                    if (this.onFieldUpdate) {
                        this.onFieldUpdate(neighbor);
                    }

                    if (neighbor.value >= neighbor.explosionThreshold) {
                        nextWave.push(neighbor);
                    }
                }

                if (this.onFieldUpdate) {
                    this.onFieldUpdate(explodingField);
                }
            }

            if (this.onExplosion) {
                this.onExplosion({ wave: 100 - maxWaves, fields: fieldsToExplode.map((f) => f.id) });
            }

            fieldsToExplode = nextWave;

            // Kurze Pause zwischen Wellen fuer Animation (konfigurierbar via localStorage)
            if (fieldsToExplode.length > 0) {
                const explosionSpeed = parseInt(localStorage.getItem('boomfection_explosion_speed'), 10) || 300;
                await new Promise((resolve) => setTimeout(resolve, explosionSpeed));
            }

            // Pruefen ob Spiel waehrend Kette beendet
            this._updateLocalPlayerStats();
            this._checkLocalEliminations();
            if (this._checkLocalWinner()) {
                return;
            }
        }

        this.state = GameState.PLAYING;
    }

    /**
     * Aktualisiert Spielerstatistiken lokal
     */
    _updateLocalPlayerStats() {
        for (const player of this.players.values()) {
            if (!player.isActive) {
                continue;
            }
            player.fieldCount = this.sphere.getPlayerFieldCount(player.id);
            let totalViruses = 0;
            for (const f of this.sphere.getPlayerFields(player.id)) {
                totalViruses += f.value;
            }
            player.totalViruses = totalViruses;
        }
    }

    /**
     * Prueft Eliminierungen lokal
     */
    _checkLocalEliminations() {
        if (this.currentRound < (this.eliminationStartRound || 2)) {
            return;
        }
        for (const player of this.players.values()) {
            if (!player.isActive) {
                continue;
            }
            if (this.sphere.getPlayerFieldCount(player.id) === 0) {
                player.eliminate();
                if (this.onPlayerEliminated) {
                    this.onPlayerEliminated(player);
                }
            }
        }
    }

    /**
     * Prueft ob ein Gewinner feststeht
     * @returns {boolean}
     */
    _checkLocalWinner() {
        const activePlayers = [...this.players.values()].filter((p) => p.isActive);
        if (activePlayers.length === 1) {
            this.winner = activePlayers[0];
            this.state = GameState.FINISHED;
            this.endTime = Date.now();
            if (this.onGameEnd) {
                this.onGameEnd({ winner: this.winner });
            }
            return true;
        }
        if (activePlayers.length === 0) {
            this.state = GameState.FINISHED;
            this.endTime = Date.now();
            if (this.onGameEnd) {
                this.onGameEnd({ winner: null });
            }
            return true;
        }
        return false;
    }

    /**
     * Wechselt zum naechsten aktiven Spieler
     */
    _nextLocalTurn() {
        const playerCount = this.playerOrder.length;
        let attempts = 0;

        do {
            this.currentPlayerIndex = (this.currentPlayerIndex + 1) % playerCount;
            attempts++;
            if (this.currentPlayerIndex === 0) {
                this.currentRound++;
            }
        } while (attempts < playerCount && !this.players.get(this.playerOrder[this.currentPlayerIndex])?.isActive);

        this.turnStartTime = Date.now();

        if (this.onTurnChange) {
            this.onTurnChange(this.getCurrentPlayer());
        }

        if (this.onStateChange) {
            this.onStateChange(this.state);
        }
    }

    // ========================================================================
    // Spieler-Verwaltung (für Server-Sync)
    // ========================================================================

    addPlayer(playerConfig) {
        // Client prüft NICHT den State - Server hat bereits validiert
        // State-Check nur für direkte Lobby-Aktionen, nicht für Server-Sync
        if (this.players.size >= this.config.maxPlayers && this.state === GameState.LOBBY) {
            return null;
        }

        // Farbe prüfen nur in Lobby (bei Sync vom Server ist das bereits korrekt)
        if (this.state === GameState.LOBBY) {
            const usedColors = [...this.players.values()].map((p) => p.colorId);
            if (usedColors.includes(playerConfig.colorId)) {
                const freeColor = PLAYER_COLORS.find((c) => !usedColors.includes(c.id));
                if (freeColor) playerConfig.colorId = freeColor.id;
            }
        }

        const player = new Player(playerConfig);
        this.players.set(player.id, player);

        // PlayerOrder nur aktualisieren wenn noch nicht vorhanden
        if (!this.playerOrder.includes(player.id)) {
            this.playerOrder.push(player.id);
        }

        if (this.players.size === 1 && !this.hostId) {
            this.hostId = player.id;
        }
        if (this.onPlayerJoin) this.onPlayerJoin(player);

        return player;
    }

    removePlayer(playerId) {
        const player = this.players.get(playerId);
        if (!player) return;

        this.players.delete(playerId);
        this.playerOrder = this.playerOrder.filter((id) => id !== playerId);

        if (this.hostId === playerId && this.players.size > 0) {
            this.hostId = this.playerOrder[0];
        }

        if (this.onPlayerLeave) this.onPlayerLeave(player);
    }

    // ========================================================================
    // Serialisierung für Server-Sync
    // ========================================================================

    serialize() {
        return {
            id: this.id,
            config: { ...this.config },
            state: this.state,
            currentRound: this.currentRound,
            currentPlayerId: this.getCurrentPlayer()?.id || null,
            hostId: this.hostId,
            players: [...this.players.values()].map((p) => p.serialize()),
            fields: this.sphere.serializeFields(),
            sphereStats: { ...this.sphere.stats },
            winner: this.winner?.serialize() || null
        };
    }
}
